import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import AcrossTabsModule, { MessageData } from 'across-tabs'

const IDLE_EVENTS: readonly string[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
  'pointerdown',
]

export const useIdleLogout = (timeoutMinutes = 15): void => {
  const timeoutRef = useRef<number | null>(null)
  const isLoggingOutRef = useRef(false)
  const tabIdRef = useRef<string>(crypto.randomUUID())

  const acrossTabsRef = useRef<InstanceType<typeof AcrossTabsModule.Parent> | null>(null)
  const broadcastRef = useRef<BroadcastChannel | null>(null)

  const auth = useAuth()

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const logout = useCallback((): void => {
    if (isLoggingOutRef.current) return
    isLoggingOutRef.current = true

    console.log('User inactive. Logging out…')

    broadcastRef.current?.postMessage({
      type: 'logout',
      source: tabIdRef.current,
    })

    auth.signoutRedirect().catch((err: unknown) => {
      console.error('Logout error:', err)
      isLoggingOutRef.current = false
    })
  }, [auth])

  const resetTimer = useCallback((): void => {
    clearTimer()
    timeoutRef.current = window.setTimeout(logout, timeoutMinutes * 60 * 1000)
  }, [clearTimer, logout, timeoutMinutes])

  useEffect(() => {
    if (!auth.isAuthenticated) {
      clearTimer()
      return
    }

    resetTimer()

    for (const event of IDLE_EVENTS) {
      window.addEventListener(event, resetTimer, { capture: true })
    }

    try {
      acrossTabsRef.current = new AcrossTabsModule.Parent({
        onChildCommunication: (message: MessageData) => {
          if (message.data === 'CHILD_ACTIVE') {
            console.log('Activity from child tab — resetting timer')
            resetTimer()
          }
        },
      })
    } catch (err) {
      console.error('AcrossTabsModule init failed:', err)
    }

    if ('BroadcastChannel' in window) {
      const bc = new BroadcastChannel('logout_channel')
      broadcastRef.current = bc

      bc.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'logout' && event.data.source !== tabIdRef.current) {
          logout()
        }
      }
    }

    return () => {
      clearTimer()

      for (const event of IDLE_EVENTS) {
        window.removeEventListener(event, resetTimer, { capture: true })
      }

      broadcastRef.current?.close()
      broadcastRef.current = null

      acrossTabsRef.current = null
    }
  }, [auth.isAuthenticated, clearTimer, resetTimer, logout])
}

export default useIdleLogout
